import { useState, useEffect } from 'react';
import AutocompleteInput, {
  Option,
} from '@/components/inputs/AutocompleteInput';
import { useBrandsOptions } from '../hooks';
import { AddBrandModal } from './index';
import { GlobalButton } from '@/components';
import { GlobalButtonVariant } from '@/components/buttons/GlobalButton';
import { Add as AddIcon } from '@mui/icons-material';

interface BrandAutocompleteInputProps {
  name: string;
  placeholder: string;
  value?: Option | null;
  onChange: (value: Option | null) => void;
  onInputChange: (value: string) => void;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  errorInfo?: string;
  autoFocus?: boolean;
  showCreateButton?: boolean;
  minWidth?: string;
}

const BrandAutocompleteInput = ({
  name,
  placeholder,
  value,
  onChange,
  onInputChange,
  required = false,
  disabled = false,
  error = false,
  errorInfo = '',
  autoFocus = false,
  showCreateButton = false,
  minWidth = 'min-w-[250px]',
}: BrandAutocompleteInputProps) => {
  const [brandSearch, setBrandSearch] = useState('');
  const [showAddBrandModal, setShowAddBrandModal] = useState(false);
  const [lastCreatedBrand, setLastCreatedBrand] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const { brandOptions, isLoading } = useBrandsOptions({
    search: brandSearch,
  });

  useEffect(() => {
    if (lastCreatedBrand) {
      const brand: Option = {
        label: lastCreatedBrand.name,
        data: lastCreatedBrand.id,
      };
      onChange(brand);
      setBrandSearch(lastCreatedBrand.name);
      setLastCreatedBrand(null);
    }
  }, [lastCreatedBrand, onChange]);

  const handleInputChange = (inputValue: string) => {
    setBrandSearch(inputValue);
    onInputChange(inputValue);
  };

  const handleAddBrandSuccess = (newBrand: { id: string; name: string }) => {
    setLastCreatedBrand(newBrand);
  };

  return (
    <div className={`flex flex-col gap-2 ${minWidth} w-full`}>
      <div className={minWidth}>
        {brandSearch.length > 0 && brandSearch.length < 2 && (
          <div className='text-gray-500 text-xs absolute -mt-5'>
            Escribe al menos 2 letras para buscar marcas.
          </div>
        )}
        <AutocompleteInput
          name={name}
          placeholder={placeholder}
          value={value}
          inputValue={value ? value.label : brandSearch}
          onChange={onChange}
          onInputChange={handleInputChange}
          options={brandOptions}
          required={required}
          disabled={disabled}
          loading={isLoading}
          error={error}
          errorInfo={errorInfo}
          autoFocus={autoFocus}
        />
      </div>
      {showCreateButton && (
        <div className='w-full [&>button]:w-full'>
          <GlobalButton
            variant={GlobalButtonVariant.secondaryButton}
            action={() => setShowAddBrandModal(true)}
            disabled={!!value}
          >
            <AddIcon color='primary' />
            Crear marca
          </GlobalButton>
        </div>
      )}
      <AddBrandModal
        open={showAddBrandModal}
        onClose={() => setShowAddBrandModal(false)}
        onSuccess={handleAddBrandSuccess}
      />
    </div>
  );
};

export default BrandAutocompleteInput;
